import { TemplateRef } from '@angular/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { asteriskMessageTypeEnum } from '../../enum/asterisk-message-type.enum.model';
import { BadgeCssClassStatusEnum } from '../../enum/badge-css-class-status.enum.model';

export class GridSettings {
  public rowHeight?: number | 'auto';
  public rowClass?: string;
  public rowClassProperty?: string;
  public headerHeight?: number;
  public footerHeight?: number;
  public limit?: number;
  public columnMode?: ColumnMode;
  public disabledProperty?: string;
  public disabledClickProperty?: string;
  public disabledCheckProperty?: string;
  public columns: GridColumn[];
  public hideCheckAll?: boolean;
  public externalSorting?: boolean;
  public internalPagination?: boolean;
  public serverPagination?: boolean;
  public noDataMessageFullSize?: boolean;
  public enableInternalNoDataMessage?: boolean;
  public asteriskMessage?: asteriskMessage;
  public disableMultiSelect?: boolean;
  public groupRowsBy?: string;
  public groupRowsByHeader?: GroupRowsHeader[];
  public groupExpansionDefault?: boolean;
  public rowDetailHeight?: number;
  public rowDetail?: RowDetail[];
  public disableGroupExpand?: boolean;
  public scrollbarH?: boolean;
  public scrollbarV?: boolean;
  public virtualization?: boolean;
  public disableInlineEdit?: boolean;
  /**
   * Opt-in: campo-riga UNIVOCO con cui ngx-datatable traccia le righe. Senza, la track è
   * l'identità dell'oggetto: righe ricostruite (spread/clone) = DOM ricreato a ogni giro.
   */
  public trackByProp?: string;
  constructor(init?: Partial<GridSettings>) {
    Object.assign(this, init);
    if (!this.columnMode) {
      this.columnMode = ColumnMode.force;
    }
    if (this.noDataMessageFullSize === undefined) {
      this.noDataMessageFullSize = true;
    }
    if (!this.rowDetailHeight) {
      this.rowDetailHeight = 200;
    }
    if (this.scrollbarH === undefined || this.scrollbarH === null) {
      this.scrollbarH = true;
    }
  }
}

export class GroupRowsHeader {
  public textValue: string;
  public prop: string;
  public order?: number;
  constructor(init?: Partial<GroupRowsHeader>) {
    Object.assign(this, init);
  }
}
export class RowDetail {
  public textValue: string;
  public prop: string;
  public order?: number;
  constructor(init?: Partial<GroupRowsHeader>) {
    Object.assign(this, init);
  }
}

export class GridColumn {
  public name: string;
  public prop: string;
  public colId?: number;
  public otherProp?: string;
  public width?: number;
  public maxWidth?: number;
  public minWidth?: number;
  public flexGrow?: number;
  public canAutoResize?: boolean;
  public type?: GridColumnTypeEnum;
  public debounceTimeInlineEdit?: number;
  /**
   * Solo numberInlineEdit, opt-in: nome del campo-riga col LIMITE SUPERIORE (per-riga).
   * I valori digitati oltre il limite vengono riportati al massimo, riscrivendo anche l'input.
   */
  public maxInlineEditProperty?: string;
  /**
   * Template della cella per le colonne `custom`. Niente `@if`/`@for` al suo interno: stampato via `ngTemplateOutlet`
   * dentro il row-detail di ngx-datatable, Angular 22.1 lancia `LContainer must be defined` a ogni tick (caso `#QtyCustom`).
   * Le condizioni si scrivono con un ternario nell'interpolazione. Vedi docs/features/order-detail/order-items-accordion.md.
   */
  public template?: TemplateRef<any>;
  public resizeable?: boolean;
  public class?: string;
  public enableClickCell?: boolean;
  public statusLabels?: string[];
  public statusLabelsWithCssClass?: StatusLabelsWithCssClass[];
  /** Testo mostrato come plain text (senza badge) quando il valore della cella non matcha nessuna entry di statusLabelsWithCssClass */
  public statusFallbackLabel?: string;
  public childProp?: string;
  public checked?: boolean;
  public checkable?: boolean;
  public sortProp?: string;
  public asteriskProp?: string;
  public order?: number;
  public disable?: boolean;
  public disableShowColumnName?: boolean;
  public hasNotificationDot?: boolean;
  public notificationDotProp?: string;
  public currencyProp?: string;
  public progressBarMaxProp?: string;
  public progressBarMaxValue?: string;
  public progressBarStriped?: boolean;
  public progressBarAnimated?: boolean;
  public progressBarColor?: "success" | "info" | "warning" | "danger" | "primary" | "secondary" | "dark";
  public singleActionIcon?: string;
  public singleActionClass?: string;
  /** Icona feather mostrata nel badge della colonna status (es. 'info'). Opzionale, opt-in. */
  public statusBadgeIcon?: string;
  /** Posizione dell'icona nel badge status ('left' default | 'right'). */
  public statusBadgeIconPosition?: 'left' | 'right';
  /** Posizione del tooltip del badge status ('left' | 'right' | ...). Opzionale. */
  public statusToolTipPlacement?: string;
  constructor(init?: Partial<GridColumn>) {
    Object.assign(this, init);
    if (!this.type) this.type = GridColumnTypeEnum.string;
  }
}

export class StatusLabelsWithCssClass {
  public label: string;
  public cssClass: BadgeCssClassStatusEnum;
  public enumValue: number;
  /** Chiave i18n del tooltip esplicativo dello status. Opzionale: assente = nessun tooltip. */
  public tooltip?: string;
  constructor(init?: StatusLabelsWithCssClass) {
    Object.assign(this, init);
  }
}

export class SortSettings {
  public prop: string;
  public dir: 'asc' | 'desc';
  public sortProp: string;

  constructor(init?: SortSettings) {
    Object.assign(this, init);
  }
}

export class asteriskMessage {
  message: string;
  type: asteriskMessageTypeEnum;
  afterMessage?: string;
  beforeMessage?: string;

  constructor(init?: asteriskMessage) {
    Object.assign(this, init);
  }
}

export enum GridColumnTypeEnum {
  string = 1,
  checkbox = 2,
  actions = 3,
  date = 4,
  status = 5,
  custom = 6,
  img = 7,
  number = 8,
  price = 9,
  toggle = 10,
  percentage = 11,
  array = 12,
  totalArray = 13,
  comingSoon = 14,
  dateTime = 15,
  rowDetail = 16,
  progressBar = 17,
  bulletedList = 18,
  stringEdit = 19,
  numberInlineEdit = 20,
  singleAction = 21,
  boolean = 22
}

export class GridCellEdited<T> {
  row: T;
  column: GridColumn;
  cacheDataSource: T[];
  constructor(init?: GridCellEdited<T>) {
    Object.assign(this, init);
  }

}
